import { PriorityStatus, PriorityType } from "@/types";
import {
  AlertCircle,
  CheckCircle2,
  Hourglass,
  ListTodo,
  PauseCircle,
  Zap,
  type LucideIcon,
} from "lucide-react";

export interface PriorityStatusMeta {
  label: string;
  icon: LucideIcon;
  className: string;
  /** Hover copy, when the label alone doesn't carry the meaning. */
  tooltip?: string;
}

/**
 * Display labels for every status — the single source of truth. The `blocked` / `waiting` *values*
 * are the BE enum and never change; only what the UI calls them does.
 *
 * `Block Int` / `Block Ext` split "blocked by someone inside Infina" from "blocked by an outside
 * party" — the old `Blocked` / `Waiting` pair gave no hint which was which.
 */
export const PRIORITY_STATUS_LABEL: Record<PriorityStatus, string> = {
  focus: "Focus",
  queue: "Queue",
  done: "Done",
  blocked: "Block Int",
  "on-hold": "On Hold",
  waiting: "Block Ext",
};

/** Only the two block statuses need explaining; the rest read for themselves. */
export const PRIORITY_STATUS_TOOLTIP: Partial<Record<PriorityStatus, string>> = {
  blocked:
    "Waiting for another Infina team member to complete a dependency before you can continue.",
  waiting:
    "Waiting for someone or an organisation outside Infina to complete a dependency before you can continue.",
};

export const priorityStatusMeta: Record<PriorityStatus, PriorityStatusMeta> = {
  focus:    { label: PRIORITY_STATUS_LABEL.focus,     icon: Zap,          className: "rounded-full bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950 dark:text-violet-300 dark:border-violet-800" },
  queue:    { label: PRIORITY_STATUS_LABEL.queue,     icon: ListTodo,     className: "rounded-full bg-muted text-muted-foreground border-border" },
  done:     { label: PRIORITY_STATUS_LABEL.done,      icon: CheckCircle2, className: "rounded-full bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800" },
  blocked:  { label: PRIORITY_STATUS_LABEL.blocked,   icon: AlertCircle,  className: "rounded-full bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800", tooltip: PRIORITY_STATUS_TOOLTIP.blocked },
  "on-hold":{ label: PRIORITY_STATUS_LABEL["on-hold"], icon: PauseCircle, className: "rounded-full bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800" },
  waiting:  { label: PRIORITY_STATUS_LABEL.waiting,   icon: Hourglass,    className: "rounded-full bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-950 dark:text-cyan-300 dark:border-cyan-800", tooltip: PRIORITY_STATUS_TOOLTIP.waiting },
};

export const getPriorityStatusMeta = (status?: PriorityStatus | null) =>
  priorityStatusMeta[status ?? "queue"];

/** Type badge classes — aligned with OneThing doc: SHP=green, WKR=amber, DAS=cyan */
export const priorityTypeBadgeClass: Record<PriorityType, string> = {
  SHP: "bg-green-100 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800",
  WKR: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800",
  DAS: "bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-950 dark:text-cyan-300 dark:border-cyan-800",
};

/** Left accent bar color per type */
export const priorityTypeAccentClass: Record<PriorityType, string> = {
  SHP: "bg-green-500",
  WKR: "bg-amber-500",
  DAS: "bg-cyan-500",
};
